import axios from "axios";
import { getCurrentBranchId } from "@/context/BranchContext";

// Always prefer same-origin in the browser so the app works regardless of the
// domain used (preview, www.fishmaamy.shop, custom domain, etc.).
// Fall back to REACT_APP_BACKEND_URL for non-browser contexts (SSR/tests).
const envUrl = process.env.REACT_APP_BACKEND_URL || "";
const origin = typeof window !== "undefined" ? window.location.origin : "";
const BACKEND_URL = origin || envUrl;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API });

// Automatically scope every request to the currently-selected branch, unless
// the caller has already provided a branch_id explicitly. Skip on /branches.
http.interceptors.request.use((config) => {
  const url = config.url || "";
  if (url.startsWith("/branches")) return config;
  const branchId = getCurrentBranchId();
  if (!branchId) return config;
  // Query params
  config.params = config.params || {};
  if (config.params.branch_id == null) {
    config.params.branch_id = branchId;
  }
  // Request body (POST/PUT)
  if (["post", "put", "patch"].includes((config.method || "").toLowerCase())) {
    if (config.data && typeof config.data === "object" && !Array.isArray(config.data)) {
      if (!config.data.branch_id) {
        config.data = { ...config.data, branch_id: branchId };
      }
    }
  }
  return config;
});

// Delivery Boys
export const listDeliveryBoys = () => http.get("/delivery-boys").then((r) => r.data);
export const createDeliveryBoy = (payload) => http.post("/delivery-boys", payload).then((r) => r.data);
export const updateDeliveryBoy = (id, payload) => http.put(`/delivery-boys/${id}`, payload).then((r) => r.data);
export const deleteDeliveryBoy = (id) => http.delete(`/delivery-boys/${id}`).then((r) => r.data);

// Collections
export const listCollections = (params = {}) => http.get("/collections", { params }).then((r) => r.data);
export const createCollection = (payload) => http.post("/collections", payload).then((r) => r.data);
export const updateCollection = (id, payload) => http.put(`/collections/${id}`, payload).then((r) => r.data);
export const deleteCollection = (id) => http.delete(`/collections/${id}`).then((r) => r.data);
export const getCollection = (id) => http.get(`/collections/${id}`).then((r) => r.data);

// Dashboard + reports
export const dashboardSummary = (target_date) =>
  http.get("/dashboard/summary", { params: target_date ? { target_date } : {} }).then((r) => r.data);
export const monthlyReport = (year, month) =>
  http.get("/reports/monthly", { params: { year, month } }).then((r) => r.data);
export const exportCsvUrl = (params = {}) => {
  const q = new URLSearchParams();
  const bId = getCurrentBranchId();
  if (bId) q.set("branch_id", bId);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, v);
  });
  const qs = q.toString();
  return `${API}/reports/export.csv${qs ? `?${qs}` : ""}`;
};

export const exportXlsxUrl = (params = {}) => {
  const q = new URLSearchParams();
  const bId = getCurrentBranchId();
  if (bId) q.set("branch_id", bId);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, v);
  });
  const qs = q.toString();
  return `${API}/reports/export.xlsx${qs ? `?${qs}` : ""}`;
};

export const exportPivotXlsxUrl = (params = {}) => {
  const q = new URLSearchParams();
  const bId = getCurrentBranchId();
  if (bId) q.set("branch_id", bId);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, v);
  });
  const qs = q.toString();
  return `${API}/reports/export-pivot.xlsx${qs ? `?${qs}` : ""}`;
};

export const paymentMethodsReport = (params = {}) =>
  http.get("/reports/payment-methods", { params }).then((r) => r.data);

export const weeklyReport = (params = {}) => http.get("/reports/weekly", { params }).then((r) => r.data);

// Branches
export const listBranches = () => http.get("/branches").then((r) => r.data);
export const createBranch = (payload) => http.post("/branches", payload).then((r) => r.data);
export const updateBranch = (id, payload) => http.put(`/branches/${id}`, payload).then((r) => r.data);
export const deleteBranch = (id) => http.delete(`/branches/${id}`).then((r) => r.data);

// Minimum Guarantee presets
export const listMinGuarantees = () => http.get("/min-guarantees").then((r) => r.data);
export const createMinGuarantee = (payload) => http.post("/min-guarantees", payload).then((r) => r.data);
export const updateMinGuarantee = (id, payload) => http.put(`/min-guarantees/${id}`, payload).then((r) => r.data);
export const deleteMinGuarantee = (id) => http.delete(`/min-guarantees/${id}`).then((r) => r.data);

// Profit
export const getProfitByDate = (entry_date) =>
  http.get("/profit-entries/by-date", { params: { entry_date } }).then((r) => r.data);
export const upsertProfitEntry = (payload) =>
  http.post("/profit-entries", payload).then((r) => r.data);
export const weeklyProfit = (params = {}) =>
  http.get("/profit-entries/weekly", { params }).then((r) => r.data);
export const listProfitEntries = (params = {}) =>
  http.get("/profit-entries", { params }).then((r) => r.data);
export const deleteProfitEntry = (id) => http.delete(`/profit-entries/${id}`).then((r) => r.data);
export const bulkDeleteProfitEntries = (ids) =>
  http.post("/profit-entries/bulk-delete", { ids }).then((r) => r.data);
export const profitBranchSummary = (params) =>
  http.get("/profit-entries/branch-summary", { params }).then((r) => r.data);

export default http;
